import React, { useState, useEffect } from 'react';
import { Typography, Breadcrumb, Card, Row, Col, Button, Upload, Input, Select, Tag, Space, Image, Tabs, List, message, Popconfirm, Modal, Form } from 'antd';
import { 
  PictureOutlined, 
  VideoCameraOutlined, 
  PlusOutlined, 
  DeleteOutlined, 
  EditOutlined,
  CopyOutlined,
  LayoutOutlined,
  FileTextOutlined,
  UploadOutlined,
  MailOutlined
} from '@ant-design/icons';
import { useAuth } from '../../../contexts/AuthContext';

const { Title, Paragraph, Text } = Typography;
const { TextArea } = Input;

const AdCreatives = () => {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState('media');
  
  // Email Templates state
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
  const [templateModalMode, setTemplateModalMode] = useState('create');
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateForm] = Form.useForm();
  const [previewVisible, setPreviewVisible] = useState(false);

  // Mock data for media library
  const [mediaItems] = useState([
    { id: 1, type: 'image', url: 'https://via.placeholder.com/300x200', name: 'Campaign Banner 1', size: '2.3 MB', date: '2024-12-01' },
    { id: 2, type: 'image', url: 'https://via.placeholder.com/300x200', name: 'Product Photo', size: '1.8 MB', date: '2024-12-02' },
    { id: 3, type: 'video', url: 'https://via.placeholder.com/300x200', name: 'Promo Video', size: '15.2 MB', date: '2024-12-03' },
    { id: 4, type: 'image', url: 'https://via.placeholder.com/300x200', name: 'Social Post', size: '1.2 MB', date: '2024-12-04' },
  ]);

  // Mock data for templates
  const [templates] = useState([
    { id: 1, name: 'Facebook Feed Ad', platform: 'facebook', category: 'feed', preview: 'https://via.placeholder.com/300x400' },
    { id: 2, name: 'Instagram Story', platform: 'instagram', category: 'story', preview: 'https://via.placeholder.com/300x400' },
    { id: 3, name: 'Google Display', platform: 'google', category: 'display', preview: 'https://via.placeholder.com/300x400' },
    { id: 4, name: 'Facebook Carousel', platform: 'facebook', category: 'carousel', preview: 'https://via.placeholder.com/300x400' },
  ]);

  // Mock data for text assets
  const [textAssets] = useState([
    { id: 1, type: 'headline', text: 'Get 50% Off Today Only!', category: 'promotional', length: 23 },
    { id: 2, type: 'description', text: 'Discover amazing deals on our premium products. Limited time offer!', category: 'promotional', length: 68 },
    { id: 3, type: 'headline', text: 'Free Shipping on All Orders', category: 'offer', length: 28 },
    { id: 4, type: 'cta', text: 'Shop Now', category: 'action', length: 8 },
    { id: 5, type: 'cta', text: 'Learn More', category: 'action', length: 10 },
  ]);

  // Load email templates
  useEffect(() => {
    const loadTemplates = async () => {
      if (!token) return;
      try {
        setLoadingTemplates(true);
        const res = await fetch('/api/templates?category=email', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error('Failed to load templates');
        const data = await res.json();
        setEmailTemplates(data || []);
      } catch (e) {
        console.error('Failed to load email templates:', e);
      } finally {
        setLoadingTemplates(false);
      }
    };
    if (activeTab === 'email') {
      loadTemplates();
    }
  }, [token, activeTab]);

  // Email template functions
  const openCreateTemplate = () => {
    templateForm.resetFields();
    templateForm.setFieldsValue({ 
      name: 'New Email Template', 
      type: 'email', 
      size: '600x', 
      emailPreset: 'newsletter', 
      subject: 'Our latest news', 
      preheader: 'Catch up with updates', 
      content: '' 
    });
    setTemplateModalMode('create');
    setEditingTemplate(null);
    setIsTemplateModalOpen(true);
  };

  const openEditTemplate = (template) => {
    setTemplateModalMode('edit');
    setEditingTemplate(template);
    templateForm.setFieldsValue({
      name: template.name,
      type: template.type || 'email',
      size: template.size || '600x',
      emailPreset: template.metadata?.preset || null,
      subject: template.metadata?.subject || '',
      preheader: template.metadata?.preheader || '',
      content: template.content || '',
    });
    setIsTemplateModalOpen(true);
    if (template.content && template.content.trim().length > 0) {
      setPreviewVisible(true);
    }
  };

  const submitTemplate = async () => {
    try {
      const values = await templateForm.validateFields();
      if (!token) throw new Error('Not authenticated');
      
      const payload = {
        name: values.name,
        type: values.type,
        size: values.size,
        category: 'email',
        metadata: {
          preset: values.emailPreset || null,
          subject: values.subject || '',
          preheader: values.preheader || ''
        },
        content: values.content || '',
      };

      if (templateModalMode === 'create') {
        const res = await fetch('/api/templates', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        });
        if (!res.ok) throw new Error('Failed to create template');
        const created = await res.json();
        setEmailTemplates(prev => [created, ...prev]);
        message.success('Email template created');
      } else if (editingTemplate) {
        const res = await fetch(`/api/templates/${editingTemplate.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        });
        if (!res.ok) throw new Error('Failed to update template');
        const updated = await res.json();
        setEmailTemplates(prev => prev.map(it => (String(it.id) === String(updated.id) ? updated : it)));
        message.success('Email template updated');
      }
      setIsTemplateModalOpen(false);
      setEditingTemplate(null);
      setPreviewVisible(false);
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e?.message || 'Operation failed');
    }
  };

  const duplicateTemplate = async (id) => {
    try {
      if (!token) throw new Error('Not authenticated');
      const res = await fetch(`/api/templates/${id}/duplicate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to duplicate template');
      const dup = await res.json();
      setEmailTemplates(prev => [dup, ...prev]);
      message.success('Template duplicated');
    } catch (e) {
      message.error(e?.message || 'Duplicate failed');
    }
  };

  const deleteTemplate = async (id) => {
    try {
      if (!token) throw new Error('Not authenticated');
      const res = await fetch(`/api/templates/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to delete template');
      setEmailTemplates(prev => prev.filter(it => String(it.id) !== String(id)));
      message.success('Template deleted');
    } catch (e) {
      message.error(e?.message || 'Delete failed');
    }
  };

  const tabItems = [
    {
      key: 'media',
      label: (
        <span>
          <PictureOutlined /> Media Library
        </span>
      ),
      children: (
        <div>
          {/* Upload Section */}
          <Card style={{ marginBottom: 24 }}>
            <Row gutter={16} align="middle">
              <Col flex="auto">
                <Title level={5} style={{ margin: 0 }}>Upload Media</Title>
                <Paragraph type="secondary" style={{ margin: 0 }}>
                  Upload images and videos for your ad campaigns
                </Paragraph>
              </Col>
              <Col>
                <Upload>
                  <Button icon={<UploadOutlined />} type="primary" size="large">
                    Upload Files
                  </Button>
                </Upload>
              </Col>
            </Row>
          </Card>

          {/* Media Grid */}
          <Row gutter={[16, 16]}>
            {mediaItems.map(item => (
              <Col xs={24} sm={12} md={8} lg={6} key={item.id}>
                <Card
                  hoverable
                  cover={
                    <div style={{ position: 'relative', paddingTop: '66.67%', background: '#f0f0f0' }}>
                      <Image
                        src={item.url}
                        alt={item.name}
                        style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }}
                        preview={true}
                      />
                      {item.type === 'video' && (
                        <VideoCameraOutlined style={{ position: 'absolute', top: 8, right: 8, fontSize: 24, color: '#fff', background: 'rgba(0,0,0,0.5)', padding: 8, borderRadius: 4 }} />
                      )}
                    </div>
                  }
                  actions={[
                    <EditOutlined key="edit" />,
                    <CopyOutlined key="copy" />,
                    <DeleteOutlined key="delete" />
                  ]}
                >
                  <Card.Meta
                    title={item.name}
                    description={
                      <Space direction="vertical" size="small" style={{ width: '100%' }}>
                        <Text type="secondary" style={{ fontSize: 12 }}>{item.size}</Text>
                        <Text type="secondary" style={{ fontSize: 12 }}>{item.date}</Text>
                      </Space>
                    }
                  />
                </Card>
              </Col>
            ))}
          </Row>
        </div>
      )
    },
    {
      key: 'templates',
      label: (
        <span>
          <LayoutOutlined /> Templates & Presets
        </span>
      ),
      children: (
        <div>
          {/* Filter Section */}
          <Card style={{ marginBottom: 24 }}>
            <Row gutter={16} align="middle">
              <Col flex="auto">
                <Space size="middle">
                  <div>
                    <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>Platform</Text>
                    <Select defaultValue="all" style={{ width: 150 }}>
                      <Select.Option value="all">All Platforms</Select.Option>
                      <Select.Option value="facebook">Facebook</Select.Option>
                      <Select.Option value="instagram">Instagram</Select.Option>
                      <Select.Option value="google">Google</Select.Option>
                    </Select>
                  </div>
                  <div>
                    <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>Category</Text>
                    <Select defaultValue="all" style={{ width: 150 }}>
                      <Select.Option value="all">All Categories</Select.Option>
                      <Select.Option value="feed">Feed</Select.Option>
                      <Select.Option value="story">Story</Select.Option>
                      <Select.Option value="carousel">Carousel</Select.Option>
                      <Select.Option value="display">Display</Select.Option>
                    </Select>
                  </div>
                </Space>
              </Col>
              <Col>
                <Button icon={<PlusOutlined />} type="primary" size="large">
                  Create Template
                </Button>
              </Col>
            </Row>
          </Card>

          {/* Templates Grid */}
          <Row gutter={[16, 16]}>
            {templates.map(template => (
              <Col xs={24} sm={12} md={8} lg={6} key={template.id}>
                <Card
                  hoverable
                  cover={
                    <div style={{ position: 'relative', paddingTop: '133%', background: '#f0f0f0' }}>
                      <Image
                        src={template.preview}
                        alt={template.name}
                        style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }}
                        preview={true}
                      />
                    </div>
                  }
                  actions={[
                    <EditOutlined key="edit" />,
                    <CopyOutlined key="copy" />,
                    <DeleteOutlined key="delete" />
                  ]}
                >
                  <Card.Meta
                    title={template.name}
                    description={
                      <Space>
                        <Tag color="blue">{template.platform}</Tag>
                        <Tag>{template.category}</Tag>
                      </Space>
                    }
                  />
                </Card>
              </Col>
            ))}
          </Row>
        </div>
      )
    },
    {
      key: 'text',
      label: (
        <span>
          <FileTextOutlined /> Text Assets
        </span>
      ),
      children: (
        <div>
          {/* Create New Text Asset */}
          <Card title="Create New Text Asset" style={{ marginBottom: 24 }}>
            <Row gutter={16}>
              <Col xs={24} md={8}>
                <div style={{ marginBottom: 16 }}>
                  <Text strong>Type</Text>
                  <Select defaultValue="headline" style={{ width: '100%', marginTop: 8 }} size="large">
                    <Select.Option value="headline">Headline</Select.Option>
                    <Select.Option value="description">Description</Select.Option>
                    <Select.Option value="cta">Call-to-Action</Select.Option>
                  </Select>
                </div>
              </Col>
              <Col xs={24} md={8}>
                <div style={{ marginBottom: 16 }}>
                  <Text strong>Category</Text>
                  <Select defaultValue="promotional" style={{ width: '100%', marginTop: 8 }} size="large">
                    <Select.Option value="promotional">Promotional</Select.Option>
                    <Select.Option value="offer">Offer</Select.Option>
                    <Select.Option value="action">Action</Select.Option>
                    <Select.Option value="informational">Informational</Select.Option>
                  </Select>
                </div>
              </Col>
              <Col xs={24} md={8}>
                <div style={{ marginBottom: 16 }}>
                  <Text strong>&nbsp;</Text>
                  <Button type="primary" size="large" icon={<PlusOutlined />} block style={{ marginTop: 8 }}>
                    Add Text Asset
                  </Button>
                </div>
              </Col>
              <Col span={24}>
                <div style={{ marginBottom: 16 }}>
                  <Text strong>Text Content</Text>
                  <TextArea 
                    rows={3} 
                    placeholder="Enter your ad text here..." 
                    style={{ marginTop: 8 }}
                    size="large"
                  />
                  <Text type="secondary" style={{ fontSize: 12, marginTop: 4, display: 'block' }}>
                    Character count: 0 / 150
                  </Text>
                </div>
              </Col>
            </Row>
          </Card>

          {/* Text Assets List */}
          <Card title="Saved Text Assets">
            <Row gutter={[16, 16]}>
              {textAssets.map(asset => (
                <Col xs={24} key={asset.id}>
                  <Card 
                    type="inner"
                    extra={
                      <Space>
                        <Tag color={asset.type === 'headline' ? 'blue' : asset.type === 'description' ? 'green' : 'orange'}>
                          {asset.type}
                        </Tag>
                        <Tag>{asset.category}</Tag>
                        <Button type="text" icon={<CopyOutlined />} size="small">Copy</Button>
                        <Button type="text" icon={<EditOutlined />} size="small">Edit</Button>
                        <Button type="text" danger icon={<DeleteOutlined />} size="small">Delete</Button>
                      </Space>
                    }
                  >
                    <Paragraph style={{ margin: 0, fontSize: 16 }}>
                      {asset.text}
                    </Paragraph>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {asset.length} characters
                    </Text>
                  </Card>
                </Col>
              ))}
            </Row>
          </Card>
        </div>
      )
    },
    {
      key: 'email',
      label: (
        <span>
          <MailOutlined /> Email Templates
        </span>
      ),
      children: (
        <div>
          {/* Create Button */}
          <Card style={{ marginBottom: 24 }}>
            <Row gutter={16} align="middle">
              <Col flex="auto">
                <Title level={5} style={{ margin: 0 }}>Email Templates</Title>
                <Paragraph type="secondary" style={{ margin: 0 }}>
                  Create and manage email templates for your campaigns
                </Paragraph>
              </Col>
              <Col>
                <Button icon={<PlusOutlined />} type="primary" size="large" onClick={openCreateTemplate}>
                  Create Email Template
                </Button>
              </Col>
            </Row>
          </Card>

          {/* Templates List */}
          <Card>
            <List
              loading={loadingTemplates}
              itemLayout="horizontal"
              dataSource={emailTemplates}
              locale={{ emptyText: 'No email templates yet. Create your first one!' }}
              renderItem={(item) => (
                <List.Item 
                  actions={[
                    <Button size="small" key="edit" onClick={() => openEditTemplate(item)}>Edit</Button>,
                    <Button size="small" key="duplicate" onClick={() => duplicateTemplate(item.id)}>Duplicate</Button>,
                    <Popconfirm 
                      key="delete" 
                      title="Delete this template?" 
                      onConfirm={() => deleteTemplate(item.id)} 
                      okText="Delete" 
                      okButtonProps={{ danger: true }}
                    >
                      <Button size="small" danger>Delete</Button>
                    </Popconfirm>
                  ]}
                >
                  <List.Item.Meta
                    title={item.name}
                    description={
                      <Space>
                        <Tag>{item.type}</Tag>
                        <Tag>{item.size}</Tag>
                        {item.metadata?.preset && <Tag color="green">{item.metadata.preset}</Tag>}
                        {item.metadata?.subject && <Tag color="purple">{item.metadata.subject}</Tag>}
                      </Space>
                    }
                  />
                </List.Item>
              )}
            />
          </Card>
        </div>
      )
    }
  ];

  return (
    <div style={{ padding: 24 }}>
      <Breadcrumb items={[{ title: 'Client' }, { title: 'Ad Creatives' }]} />
      
      <div style={{ marginTop: 12, marginBottom: 24 }}>
        <Title level={3} style={{ margin: 0 }}>Ad Creatives</Title>
        <Paragraph type="secondary" style={{ marginTop: 8 }}>
          Manage your media, templates, and text assets for ad campaigns
        </Paragraph>
      </div>

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={tabItems}
        size="large"
        type="card"
      />

      {/* Email Template Modal */}
      <Modal
        title={templateModalMode === 'create' ? 'Create Email Template' : 'Edit Email Template'}
        open={isTemplateModalOpen}
        onOk={submitTemplate}
        onCancel={() => {
          setIsTemplateModalOpen(false);
          setEditingTemplate(null);
          setPreviewVisible(false);
        }}
        destroyOnClose
        width={1000}
      >
        <div style={{ display: 'flex', gap: 24 }}>
          <div style={{ flex: 1 }}>
            <Form form={templateForm} layout="vertical">
              <Form.Item label="Name" name="name" rules={[{ required: true, message: 'Please enter a name' }]}>
                <Input placeholder="Template name" />
              </Form.Item>
              <Form.Item label="Type" name="type" rules={[{ required: true }]}>
                <Select options={[{ value: 'email', label: 'Email' }]} />
              </Form.Item>
              <Form.Item label="Size" name="size" tooltip="Typical email width is ~600px">
                <Input placeholder="e.g., 600x" />
              </Form.Item>
              <Form.Item label="Preset" name="emailPreset">
                <Select
                  options={[
                    { value: 'newsletter', label: 'Newsletter' },
                    { value: 'promotion', label: 'Promotion' },
                    { value: 'transactional', label: 'Transactional' },
                  ]}
                  onChange={(val) => {
                    if (val === 'newsletter') {
                      templateForm.setFieldsValue({ subject: 'Our latest news', preheader: 'Catch up with updates' });
                    } else if (val === 'promotion') {
                      templateForm.setFieldsValue({ subject: 'Limited-time offer inside', preheader: 'Save big before it ends' });
                    } else if (val === 'transactional') {
                      templateForm.setFieldsValue({ subject: 'Your receipt', preheader: 'Thanks for your purchase' });
                    }
                  }}
                />
              </Form.Item>
              <Form.Item label="Subject" name="subject">
                <Input placeholder="Email subject" />
              </Form.Item>
              <Form.Item label="Preheader" name="preheader">
                <Input placeholder="Email preheader" />
              </Form.Item>
              <Form.Item label="Email HTML" name="content">
                <TextArea rows={8} placeholder="Paste HTML content here" />
              </Form.Item>
              <Form.Item>
                <Button
                  type="default"
                  onClick={() => {
                    const html = templateForm.getFieldValue('content');
                    if (!html) {
                      message.warning('No HTML content to preview');
                      return;
                    }
                    setPreviewVisible(true);
                  }}
                >
                  Preview Email
                </Button>
              </Form.Item>
            </Form>
          </div>
          {previewVisible && (
            <div style={{ flex: 1 }}>
              <Title level={5} style={{ marginBottom: 8 }}>Preview</Title>
              <div
                style={{
                  border: '1px solid #e5e7eb',
                  borderRadius: 4,
                  padding: 16,
                  maxHeight: 500,
                  overflow: 'auto',
                  background: '#ffffff',
                  color: '#000000',
                }}
                dangerouslySetInnerHTML={{ __html: templateForm.getFieldValue('content') || '' }}
              />
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default AdCreatives;
